import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps the "Request a Quote" link pointed at the variant the shopper is
 * currently looking at.
 *
 * Mirrors the listener pattern used by product-sku.js: the product information
 * section dispatches a productSelect event carrying a promise that resolves
 * with the newly selected variant.
 *
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement} link - The quote request link.
 *
 * @extends {Component<Refs>}
 */
class GhQuoteButton extends Component {
  requiredRefs = ['link'];

  #target = null;

  connectedCallback() {
    super.connectedCallback();
    this.#target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!this.#target) return;
    this.#target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#target) return;
    this.#target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#target = null;
  }

  /**
   * @param {{ promise: Promise<{ detail: any }> }} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
          if (newProduct.handle) this.dataset.productHandle = newProduct.handle;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) return;
        if (!resource) return;

        this.#updateHref(resource.id);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[gh-quote-button] Event promise rejected:', error);
      });
  };

  /**
   * Rebuilds the link so the quote form receives the current variant.
   * @param {string|number|undefined} variantId
   */
  #updateHref(variantId) {
    const { link } = this.refs;
    const base = this.dataset.quoteUrl;
    const handle = this.dataset.productHandle;
    if (!link || !base) return;

    // Relative base: resolve against the current origin, then emit a
    // root-relative href so localisation prefixes are preserved.
    const url = new URL(base, window.location.origin);
    if (handle) url.searchParams.set('product', handle);
    if (variantId) url.searchParams.set('variant', String(variantId));

    link.href = `${url.pathname}${url.search}`;
  }
}

if (!customElements.get('gh-quote-button')) {
  customElements.define('gh-quote-button', GhQuoteButton);
}
